/**
 * MobileJobTimeInput.js
 * 모바일 투입시간 입력 페이지 (User용)
 * Mobile First 전략 - 390px 기준
 * 
 * 주의: 캘린더 기능은 제거되었습니다. TimeCalendar 컴포넌트는 unused_files로 이동되었습니다.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Fab,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  TextField,
} from '@mui/material';
import { Plus, Clock } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useMobileView } from '../contexts/MobileViewContext';
import MobileTodoDrawer from '../components/MobileTodoDrawer';
import LogEntryModal from '../components/LogEntryModal';
import axios from 'axios';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWithinInterval, parseISO } from 'date-fns';
import { ko } from 'date-fns/locale';
import apiConfig from '../config/api';

const API_BASE_URL = apiConfig.V1;

const MobileJobTimeInput = () => {
  const { colors, isDark } = useCustomTheme();
  const { isMobileView } = useMobileView();
  const [timelogs, setTimelogs] = useState([]);
  const [todos, setTodos] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [todoDrawerOpen, setTodoDrawerOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weeklyTimelogs, setWeeklyTimelogs] = useState([]);
  const [weeklyTodos, setWeeklyTodos] = useState([]);
  const [weeklyActivity, setWeeklyActivity] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  });

  // 투입시간 조회 (날짜 범위)
  const fetchTimelogs = useCallback(async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate && endDate) {
        params.start_date = format(new Date(startDate), 'yyyy-MM-dd');
        params.end_date = format(new Date(endDate), 'yyyy-MM-dd');
      } else if (startDate) {
        params.date = format(new Date(startDate), 'yyyy-MM-dd');
      }
      
      const response = await axios.get(`${API_BASE_URL}/timelogs/my`, { params });
      setTimelogs(response.data || []);
    } catch (error) {
      console.error('투입시간 조회 실패:', error);
    }
  }, []);

  // 이번 주 투입시간 조회
  const fetchWeeklyTimelogs = useCallback(async () => {
    try {
      const today = new Date();
      const weekStart = startOfWeek(today, { weekStartsOn: 1 }); // 월요일 시작
      const weekEnd = endOfWeek(today, { weekStartsOn: 1 }); // 일요일 종료
      
      const response = await axios.get(`${API_BASE_URL}/timelogs/my`, {
        params: {
          start_date: format(weekStart, 'yyyy-MM-dd'),
          end_date: format(weekEnd, 'yyyy-MM-dd')
        }
      });
      setWeeklyTimelogs(response.data || []);
    } catch (error) {
      console.error('이번 주 투입시간 조회 실패:', error);
      setWeeklyTimelogs([]);
    }
  }, []);

  // 이번 주 To-Do 조회
  const fetchWeeklyTodos = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/todos`);
      const allTodos = response.data || [];
      
      // 이번 주에 생성된 To-Do만 필터링
      const today = new Date();
      const weekStart = startOfWeek(today, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
      
      const weeklyTodosFiltered = allTodos.filter(todo => {
        if (!todo.created_at) return false;
        const createdDate = parseISO(todo.created_at.split('T')[0]);
        return isWithinInterval(createdDate, { start: weekStart, end: weekEnd });
      });
      
      setWeeklyTodos(weeklyTodosFiltered);
    } catch (error) {
      console.error('이번 주 To-Do 조회 실패:', error);
      setWeeklyTodos([]);
    }
  }, []);

  // 이번 주 활발도 계산
  const calculateWeeklyActivity = useCallback(() => {
    // 이번 주 투입시간 입력한 고유 일수 계산
    const uniqueDates = new Set();
    weeklyTimelogs.forEach(log => {
      if (log.log_date) {
        uniqueDates.add(log.log_date);
      }
    });
    const timeInputDays = uniqueDates.size;
    
    // 이번 주 To-Do 생성 개수
    const todoCount = weeklyTodos.length;
    
    // 활발도 평가
    let activityText = '';
    let activityColor = '';
    
    if (timeInputDays >= 5 && todoCount >= 3) {
      activityText = '이번 주 매우 활발';
      activityColor = '#4CAF50'; // 초록색
    } else if (timeInputDays >= 4 || todoCount >= 2) {
      activityText = '이번 주 활발';
      activityColor = '#2196F3'; // 파란색
    } else if (timeInputDays >= 2 || todoCount >= 1) {
      activityText = '이번 주 보통';
      activityColor = '#FF9800'; // 주황색
    } else if (timeInputDays >= 1) {
      activityText = '이번 주 저조';
      activityColor = '#9E9E9E'; // 회색
    } else {
      activityText = '이번 주 미기록';
      activityColor = '#BDBDBD'; // 연한 회색
    }
    
    setWeeklyActivity({ text: activityText, color: activityColor });
  }, [weeklyTimelogs, weeklyTodos]);

  // To-Do 조회
  const fetchTodos = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/todos`, {
        params: { status: 'active' }
      });
      setTodos(response.data || []);
    } catch (error) {
      console.error('To-Do 조회 실패:', error);
    }
  }, []);

  // 계약 목록 조회
  const fetchContracts = useCallback(async () => {
    try {
      const recentResponse = await axios.get(`${API_BASE_URL}/contracts/recent`, {
        params: { limit: 10 }
      });
      setContracts(recentResponse.data || []);
    } catch (error) {
      console.error('계약 조회 실패:', error);
    }
  }, []);

  // 업체 목록 조회
  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/customers`);
      setCompanies(response.data || []);
    } catch (error) {
      console.error('업체 조회 실패:', error);
    }
  }, []);

  // 날짜 범위 변경 시 투입시간 조회
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      fetchTimelogs(dateRange.startDate, dateRange.endDate);
    }
  }, [dateRange, fetchTimelogs]);

  useEffect(() => {
    fetchTodos();
    fetchContracts();
    fetchCompanies();
    fetchWeeklyTimelogs();
    fetchWeeklyTodos();
  }, [fetchTodos, fetchContracts, fetchCompanies, fetchWeeklyTimelogs, fetchWeeklyTodos]);

  // 이번 주 활발도 계산
  useEffect(() => {
    calculateWeeklyActivity();
  }, [calculateWeeklyActivity]);

  // 투입시간 항목 클릭 핸들러
  const handleLogClick = (log) => {
    const contract = contracts.find(c => c.contract_id === log.contract_id);
    const company = log.company_code ? companies.find(c => c.company_code === log.company_code) : null;
    setSelectedLog({
      id: log.timelog_id,
      ...log,
      contract: contract,
      company: company,
    });
    setIsModalOpen(true);
  };

  // 모달 저장 핸들러
  const handleSave = async (data, logId) => {
    try {
      if (logId) {
        // 수정
        await axios.put(`${API_BASE_URL}/timelogs/${logId}`, data);
      } else {
        // 생성
        await axios.post(`${API_BASE_URL}/timelogs`, data);
      }
      // 현재 선택된 날짜 범위로 다시 조회
      if (dateRange.startDate && dateRange.endDate) {
        await fetchTimelogs(dateRange.startDate, dateRange.endDate);
      }
      await fetchWeeklyTimelogs(); // 이번 주 데이터 갱신
      await fetchWeeklyTodos(); // 이번 주 To-Do 데이터 갱신
      setIsModalOpen(false);
      setSelectedLog(null);
      // 오늘 업무시간 업데이트 이벤트 발생
      window.dispatchEvent(new Event('timelogUpdated'));
    } catch (error) {
      alert(error.response?.data?.detail || '저장 실패');
    }
  };

  // 모달 삭제 핸들러
  const handleDelete = async (logId) => {
    try {
      await axios.delete(`${API_BASE_URL}/timelogs/${logId}`);
      // 현재 선택된 날짜 범위로 다시 조회
      if (dateRange.startDate && dateRange.endDate) {
        await fetchTimelogs(dateRange.startDate, dateRange.endDate);
      }
      await fetchWeeklyTimelogs(); // 이번 주 데이터 갱신
      setIsModalOpen(false);
      setSelectedLog(null);
      // 오늘 업무시간 업데이트 이벤트 발생
      window.dispatchEvent(new Event('timelogUpdated'));
    } catch (error) {
      alert(error.response?.data?.detail || '삭제 실패');
    }
  };

  // 모달 닫기
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLog(null);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.primaryBg,
        position: 'relative',
        minHeight: 'calc(100vh - 72px - 96px)',
        pb: 12,
      }}
    >
      {/* 헤더 */}
      <Box
        sx={{
          pt: 4,
          px: 3,
          pb: 2,
          borderBottom: `1px solid ${colors.primaryBorder}`,
          backgroundColor: isDark ? '#1C1D22' : '#FFFFFF',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: colors.primaryText, 
              fontWeight: 600, 
              fontSize: '16px',
              lineHeight: 1.4,
            }}
          >
            투입시간 입력
          </Typography>
          {weeklyActivity && (
            <Chip
              label={weeklyActivity.text}
              size="small"
              sx={{
                fontSize: '12px',
                height: '26px',
                fontWeight: 600,
                backgroundColor: weeklyActivity.color,
                color: '#FFFFFF',
                '& .MuiChip-label': {
                  px: 1.5,
                },
              }}
            />
          )}
        </Box>
        
        {/* 날짜 필터 */}
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* 빠른 선택 배지 */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label="일주일"
              onClick={() => {
                const today = new Date();
                const weekStart = startOfWeek(today, { weekStartsOn: 1 });
                const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
                setDateRange({
                  startDate: format(weekStart, 'yyyy-MM-dd'),
                  endDate: format(weekEnd, 'yyyy-MM-dd'),
                });
              }}
              size="small"
              sx={{
                fontSize: '12px',
                height: '28px',
                cursor: 'pointer',
                backgroundColor: dateRange.startDate && dateRange.endDate && 
                  format(new Date(dateRange.startDate), 'yyyy-MM-dd') === format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd') &&
                  format(new Date(dateRange.endDate), 'yyyy-MM-dd') === format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
                  ? colors.accent
                  : isDark ? '#2A2B30' : '#F0F0F0',
                color: dateRange.startDate && dateRange.endDate && 
                  format(new Date(dateRange.startDate), 'yyyy-MM-dd') === format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd') &&
                  format(new Date(dateRange.endDate), 'yyyy-MM-dd') === format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
                  ? '#FFFFFF'
                  : colors.primaryText,
                '&:hover': {
                  backgroundColor: colors.accent,
                  color: '#FFFFFF',
                },
              }}
            />
            <Chip
              label="한달"
              onClick={() => {
                const today = new Date();
                const monthStart = startOfMonth(today);
                const monthEnd = endOfMonth(today);
                setDateRange({
                  startDate: format(monthStart, 'yyyy-MM-dd'),
                  endDate: format(monthEnd, 'yyyy-MM-dd'),
                });
              }}
              size="small"
              sx={{
                fontSize: '12px',
                height: '28px',
                cursor: 'pointer',
                backgroundColor: dateRange.startDate && dateRange.endDate && 
                  format(new Date(dateRange.startDate), 'yyyy-MM-dd') === format(startOfMonth(new Date()), 'yyyy-MM-dd') &&
                  format(new Date(dateRange.endDate), 'yyyy-MM-dd') === format(endOfMonth(new Date()), 'yyyy-MM-dd')
                  ? colors.accent
                  : isDark ? '#2A2B30' : '#F0F0F0',
                color: dateRange.startDate && dateRange.endDate && 
                  format(new Date(dateRange.startDate), 'yyyy-MM-dd') === format(startOfMonth(new Date()), 'yyyy-MM-dd') &&
                  format(new Date(dateRange.endDate), 'yyyy-MM-dd') === format(endOfMonth(new Date()), 'yyyy-MM-dd')
                  ? '#FFFFFF'
                  : colors.primaryText,
                '&:hover': {
                  backgroundColor: colors.accent,
                  color: '#FFFFFF',
                },
              }}
            />
          </Box>
          
          {/* 시작일~종료일 선택 */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
            <TextField
              type="date"
              label="시작일"
              value={dateRange.startDate || ''}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value || null }))}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  fontSize: '14px',
                  minHeight: '40px',
                  color: colors.primaryText,
                },
                '& .MuiInputLabel-root': {
                  fontSize: '12px',
                  color: colors.secondaryText,
                },
                '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: isDark ? 'invert(1)' : 'none',
                  opacity: 1,
                  cursor: 'pointer',
                },
                '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                  opacity: 0.7,
                },
              }}
              inputProps={{
                sx: {
                  color: colors.primaryText,
                  '&::-webkit-calendar-picker-indicator': {
                    filter: isDark ? 'invert(1)' : 'none',
                  },
                }
              }}
            />
            <Typography variant="body2" sx={{ color: colors.secondaryText, fontSize: '14px' }}>
              ~
            </Typography>
            <TextField
              type="date"
              label="종료일"
              value={dateRange.endDate || ''}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value || null }))}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  fontSize: '14px',
                  minHeight: '40px',
                  color: colors.primaryText,
                },
                '& .MuiInputLabel-root': {
                  fontSize: '12px',
                  color: colors.secondaryText,
                },
                '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: isDark ? 'invert(1)' : 'none',
                  opacity: 1,
                  cursor: 'pointer',
                },
                '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                  opacity: 0.7,
                },
              }}
              inputProps={{
                sx: {
                  color: colors.primaryText,
                  '&::-webkit-calendar-picker-indicator': {
                    filter: isDark ? 'invert(1)' : 'none',
                  },
                }
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* 투입시간 목록 */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch', // iOS 부드러운 스크롤
        touchAction: 'pan-y', // 수직 스크롤만 허용
        px: 3, 
        py: 2,
        minHeight: 0,
      }}>
        {timelogs.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <Clock size={48} style={{ color: colors.secondaryText, marginBottom: 16 }} />
            <Typography variant="body1" sx={{ color: colors.secondaryText, mb: 1 }}>
              오늘 입력된 시간이 없습니다
            </Typography>
            <Typography variant="caption" sx={{ color: colors.tertiaryText }}>
              + 버튼을 눌러 투입시간을 추가하세요
            </Typography>
          </Box>
        ) : (
          <List>
            {timelogs.map((log) => {
              const company = log.company_code 
                ? companies.find(c => c.company_code === log.company_code)
                : null;
              const contract = log.contract_id
                ? contracts.find(c => c.contract_id === log.contract_id)
                : null;
              
              return (
                <Paper
                  key={log.timelog_id}
                  onClick={() => handleLogClick(log)}
                  sx={{
                    p: 2.5,
                    mb: 2,
                    backgroundColor: isDark ? '#25262B' : '#FFFFFF',
                    border: `1px solid ${colors.primaryBorder}`,
                    borderRadius: 2,
                    boxShadow: isDark 
                      ? '0 2px 8px rgba(0,0,0,0.2)' 
                      : '0 2px 8px rgba(0,0,0,0.06)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    '&:active': {
                      transform: 'scale(0.98)',
                      boxShadow: isDark 
                        ? '0 1px 4px rgba(0,0,0,0.15)' 
                        : '0 1px 4px rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.primaryText,
                          fontWeight: 600,
                          mb: 1,
                          fontSize: '15px',
                        }}
                      >
                        {log.work_description || log.input_description || '업무 설명 없음'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                        {company?.company_name_kr && (
                          <Chip
                            label={company.company_name_kr}
                            size="small"
                            sx={{ 
                              fontSize: '11px',
                              height: '24px',
                              padding: '0 8px',
                            }}
                          />
                        )}
                        {contract?.contract_title && (
                          <Chip
                            label={contract.contract_title}
                            size="small"
                            sx={{ 
                              fontSize: '11px',
                              height: '24px',
                              padding: '0 8px',
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 2 }}>
                      {log.log_date && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.secondaryText,
                            fontSize: '11px',
                            fontWeight: 500,
                            mb: 0.5,
                          }}
                        >
                          {format(new Date(log.log_date), 'yyyy/MM/dd', { locale: ko })}
                        </Typography>
                      )}
                      <Typography
                        variant="h6"
                        sx={{
                          color: colors.accent,
                          fontWeight: 700,
                          fontSize: '18px',
                        }}
                      >
                        {Math.floor((log.input_minute || 0) / 60)}h {((log.input_minute || 0) % 60)}m
                      </Typography>
                    </Box>
                  </Box>
                  {log.input_description && log.input_description !== log.work_description && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.secondaryText,
                        fontSize: '12px',
                        display: 'block',
                        mt: 1,
                      }}
                    >
                      {log.input_description}
                    </Typography>
                  )}
                </Paper>
              );
            })}
          </List>
        )}
      </Box>

      {/* [모바일 최적화] 엄지손가락 영역에 + 버튼 고정 */}
      <Fab
        color="primary"
        aria-label="add"
        onClick={() => {
          setSelectedLog({
            log_date: format(currentDate, 'yyyy-MM-dd'),
          });
          setIsModalOpen(true);
        }}
        sx={{
          position: 'fixed',
          bottom: '104px', // 하단 네비게이션 위 (72px + 32px 여백)
          right: '20px',
          backgroundColor: colors.accent,
          '&:hover': {
            backgroundColor: colors.accent,
            opacity: 0.9,
          },
          minWidth: '64px', // 터치 영역 확대
          minHeight: '64px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 1100,
        }}
      >
        <Plus size={28} />
      </Fab>

      {/* To-Do 리스트가 담긴 하단 서랍 */}
      <MobileTodoDrawer
        open={todoDrawerOpen}
        onOpen={() => setTodoDrawerOpen(true)}
        onClose={() => setTodoDrawerOpen(false)}
        onTodoDragStart={(todo) => {
          // 드래그 시작 처리 (캘린더 제거로 사용 안 함)
        }}
        onTodoClick={(todo) => {
          const contract = contracts.find(c => c.contract_id === todo.contract_id);
          const company = todo.company_code ? companies.find(c => c.company_code === todo.company_code) : null;
          setSelectedLog({
            fromTodo: true,
            todoId: todo.todo_id,
            log_date: format(currentDate, 'yyyy-MM-dd'),
            company: company,
            company_code: todo.company_code,
            contract: contract,
            input_description: todo.note_1 || '',
            input_minute: 60,
          });
          setIsModalOpen(true);
          setTodoDrawerOpen(false);
        }}
        onTodoSelect={(todo) => {
          const contract = contracts.find(c => c.contract_id === todo.contract_id);
          const company = todo.company_code ? companies.find(c => c.company_code === todo.company_code) : null;
          setSelectedLog({
            fromTodo: true,
            todoId: todo.todo_id,
            log_date: format(currentDate, 'yyyy-MM-dd'),
            company: company,
            company_code: todo.company_code,
            contract: contract,
            input_description: todo.note_1 || '',
            input_minute: 60,
          });
          setIsModalOpen(true);
          setTodoDrawerOpen(false);
        }}
      />

      {/* 프로젝트 검색, 시간 입력 폼이 포함된 모달 (Bottom Sheet) */}
      <LogEntryModal
        open={isModalOpen}
        onClose={handleCloseModal}
        logData={selectedLog}
        contracts={contracts}
        companies={companies}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </Box>
  );
};

export default MobileJobTimeInput;

